import React, { useState, useEffect } from 'react';
import { Select, Button, Input, Upload, message, Space, Divider } from 'antd';
import { UploadOutlined, ApiOutlined, DatabaseOutlined, FileTextOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Option } = Select;
const { TextArea } = Input;

const DataSourceConnector = ({ onDataSourceChange }) => {
  const [connectionType, setConnectionType] = useState('api');
  const [apiUrl, setApiUrl] = useState('');
  const [csvData, setCsvData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [availableDatasets, setAvailableDatasets] = useState([]);

  // Mock datasets for demonstration
  const mockDatasets = [
    {
      id: 'sales_data',
      name: 'Sales Data',
      description: 'Monthly sales figures by region',
      fields: [
        { name: 'month', type: 'string' },
        { name: 'region', type: 'string' },
        { name: 'sales', type: 'number' },
        { name: 'profit', type: 'number' },
        { name: 'customers', type: 'number' }
      ]
    },
    {
      id: 'user_analytics',
      name: 'User Analytics',
      description: 'Website user behavior data',
      fields: [
        { name: 'date', type: 'date' },
        { name: 'page_views', type: 'number' },
        { name: 'unique_visitors', type: 'number' },
        { name: 'bounce_rate', type: 'number' },
        { name: 'source', type: 'string' }
      ]
    },
    {
      id: 'product_performance',
      name: 'Product Performance',
      description: 'Product sales and ratings',
      fields: [
        { name: 'product_name', type: 'string' },
        { name: 'category', type: 'string' },
        { name: 'price', type: 'number' },
        { name: 'units_sold', type: 'number' },
        { name: 'rating', type: 'number' },
        { name: 'reviews', type: 'number' }
      ]
    }
  ];

  useEffect(() => {
    // Load available datasets
    setAvailableDatasets(mockDatasets);
  }, []);

  const handleApiConnection = async () => {
    if (!apiUrl) {
      message.error('Please enter an API URL');
      return;
    }

    setLoading(true);
    try {
      // Try to connect to the API and get schema
      const response = await axios.get(apiUrl);
      const data = response.data;
      
      // Infer field types from the first row of data
      const fields = inferFieldTypes(data);
      
      onDataSourceChange({
        type: 'api',
        url: apiUrl,
        name: 'API Data'
      }, fields);
      
      message.success('Connected to API successfully');
    } catch (error) {
      console.error('API connection error:', error);
      message.error('Failed to connect to API');
    } finally {
      setLoading(false);
    }
  };

  const handleDatasetSelection = (datasetId) => {
    const dataset = availableDatasets.find(d => d.id === datasetId);
    if (dataset) {
      onDataSourceChange({
        type: 'dataset',
        id: datasetId,
        name: dataset.name
      }, dataset.fields);
      
      message.success(`Connected to ${dataset.name}`);
    }
  };

  const handleFileUpload = (info) => {
    if (info.file.status === 'uploading') {
      setLoading(true);
      return;
    }
    
    if (info.file.status === 'done') {
      const file = info.file.originFileObj;
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          const parsedData = parseCSV(text);
          const fields = inferFieldTypes(parsedData);
          
          setCsvData(parsedData);
          onDataSourceChange({
            type: 'csv',
            name: file.name,
            data: parsedData
          }, fields);
          
          message.success('CSV file uploaded successfully');
        } catch (error) {
          message.error('Failed to parse CSV file');
        } finally {
          setLoading(false);
        }
      };
      
      reader.readAsText(file);
    }
    
    if (info.file.status === 'error') {
      setLoading(false);
      message.error('File upload failed');
    }
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    
    return lines.slice(1).map(line => {
      const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      return row;
    });
  };

  const inferFieldTypes = (data) => {
    if (!data || data.length === 0) return [];
    
    const firstRow = data[0];
    return Object.keys(firstRow).map(key => {
      const value = firstRow[key];
      let type = 'string';
      
      // Try to infer type from the value
      if (!isNaN(value) && !isNaN(parseFloat(value))) {
        type = 'number';
      } else if (Date.parse(value)) {
        type = 'date';
      }
      
      return { name: key, type };
    });
  };

  const uploadProps = {
    name: 'file',
    accept: '.csv,.json',
    beforeUpload: () => false, // Prevent auto upload
    onChange: handleFileUpload,
    showUploadList: false,
  };

  return (
    <div>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Select
          value={connectionType}
          onChange={setConnectionType}
          style={{ width: '100%' }}
        >
          <Option value="dataset">
            <DatabaseOutlined /> Predefined Datasets
          </Option>
          <Option value="api">
            <ApiOutlined /> API Endpoint
          </Option>
          <Option value="file">
            <FileTextOutlined /> Upload File
          </Option>
        </Select>

        {connectionType === 'dataset' && (
          <div>
            <Select
              placeholder="Select a dataset"
              style={{ width: '100%' }}
              onChange={handleDatasetSelection}
            >
              {availableDatasets.map(dataset => (
                <Option key={dataset.id} value={dataset.id}>
                  <div>
                    <div>{dataset.name}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {dataset.description}
                    </div>
                  </div>
                </Option>
              ))}
            </Select>
          </div>
        )}

        {connectionType === 'api' && (
          <div>
            <Input
              placeholder="Enter API URL (e.g., https://api.example.com/data)"
              value={apiUrl}
              onChange={(e) => setApiUrl(e.target.value)}
            />
            <Button
              type="primary"
              onClick={handleApiConnection}
              loading={loading}
              style={{ marginTop: 8, width: '100%' }}
            >
              Connect to API
            </Button>
          </div>
        )}

        {connectionType === 'file' && (
          <div>
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />} loading={loading} block>
                Upload CSV/JSON File
              </Button>
            </Upload>
            <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
              Supported formats: CSV, JSON
            </div>
          </div>
        )}
      </Space>
    </div>
  );
};

export default DataSourceConnector;